/**
 * Company email (ADR-023) — send from the Company's own domain instead of
 * noreply@fias.io.
 *
 * Three steps, in the order a Company admin does them — one card each, always
 * all three, so the whole path is visible from the start; a step that cannot
 * be used yet shows one greyed line saying what it is waiting for:
 *
 *   1. DOMAINS. Only the domains Fias approved can be added. Each shows the DNS
 *      records to publish — three DKIM CNAMEs plus a MAIL FROM MX and SPF on a
 *      `bounce.` subdomain. None of them touch the Company's existing mail.
 *   2. SENDER ADDRESSES on a domain (team@…), with the name recipients see.
 *   3. ARCHES ALLOWED TO SEND as one of those addresses: the ones the server
 *      lists as eligible (the first-party adopters such as Document Sign, and
 *      any approved arche whose approval covers the email permission), with
 *      how far its mail may reach.
 *
 * Rendered only when the Company holds `company_email`; hidden, not disabled,
 * otherwise (the same reason as the Branding card). A failed load is shown,
 * never hidden — an admin must not read an outage as "no email feature". The
 * server enforces every rule here; this is the affordance.
 */

import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Info } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button, buttonVariants } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from '@/components/ui/use-toast';
import {
  api,
  ApiError,
  type CompanyEmailDto,
  type CompanyEmailDomainStatsDto,
  type CompanySendingDomainDto,
  type CompanyDnsRecordCheckDto,
} from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

const SCOPE_BADGE: Record<'workspace' | 'company' | 'any', string> = {
  workspace: 'Its workspace only',
  company: 'Whole Company',
  any: 'Anyone',
};
const SELECT_CLASS =
  'h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring';

const STATUS_LABEL: Record<CompanySendingDomainDto['status'], string> = {
  pending_dns: 'Waiting for DNS',
  verified: 'Verified',
  failed: 'Needs attention',
  paused: 'Paused by Fias',
  removed: 'Removed',
};

function errorText(err: unknown): string {
  if (err instanceof ApiError) return err.message.replace(/^[A-Z_]+:\s*/, '');
  return 'Something went wrong. Please try again.';
}

export function CompanyEmailCard({ companyId }: { companyId: string }) {
  const qc = useQueryClient();
  const email = useQuery({
    queryKey: queryKeys.companies.email(companyId),
    queryFn: () => api.getCompanyEmail(companyId),
    // While Fias is putting records into (or taking them out of) the Company's
    // zone, keep up with the reconciler rather than waiting for a refocus.
    refetchInterval: (q) =>
      q.state.data?.domains.some((d) =>
        d.records.some(
          (r) => r.published?.state === 'pending' || r.published?.state === 'deleting',
        ),
      )
        ? 10_000
        : false,
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: queryKeys.companies.email(companyId) });
    // Linked workspaces show their owners which arches send as the Company.
    void qc.invalidateQueries({ queryKey: queryKeys.vault.companyAll });
    // Removing a sending domain retires the rows Fias published in the Web tab's zone.
    void qc.invalidateQueries({ queryKey: queryKeys.companies.web(companyId) });
  };
  const run = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onSuccess: refresh,
    onError: (err) =>
      toast({ title: 'Could not save', description: errorText(err), variant: 'destructive' }),
  });

  if (email.isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Email sending</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-2">
          <p role="alert" className="text-sm text-destructive">
            Could not load your email settings. {errorText(email.error)}
          </p>
          <Button size="sm" variant="outline" onClick={() => void email.refetch()}>
            Try again
          </Button>
        </CardContent>
      </Card>
    );
  }
  if (!email.data?.enabled) return null;
  const data = email.data;

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Send from your own domain instead of noreply@fias.io: add a domain Fias approved for you,
        create the addresses you send from, then choose which arches may use them.
      </p>
      {data.pausedByFias && (
        <p role="status" className="rounded-md border border-destructive/40 p-3 text-sm">
          Fias has temporarily paused all Company email. Nothing is sent from your domain until it
          resumes. Meanwhile new Document Sign envelopes can&apos;t be sent (updates on envelopes
          already out still go from Fias, with your branding), and arches that send as you are
          refused.
        </p>
      )}
      <DomainsSection companyId={companyId} data={data} run={run.mutate} busy={run.isPending} />
      <IdentitiesSection companyId={companyId} data={data} run={run.mutate} busy={run.isPending} />
      <AuthorizationsSection
        companyId={companyId}
        data={data}
        run={run.mutate}
        busy={run.isPending}
      />
    </div>
  );
}

/**
 * One setup step. `waitingFor` renders the step greyed with a single line
 * naming what it needs first, instead of hiding it — a partner should see the
 * whole path, not discover step 2 only after finishing step 1.
 */
function StepCard({
  title,
  description,
  waitingFor,
  children,
}: {
  title: string;
  description: string;
  waitingFor?: string;
  children?: React.ReactNode;
}) {
  return (
    <Card aria-disabled={waitingFor ? true : undefined} className={waitingFor ? 'opacity-60' : ''}>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {waitingFor ? <p className="text-sm text-muted-foreground">{waitingFor}</p> : children}
      </CardContent>
    </Card>
  );
}

type SectionProps = {
  companyId: string;
  data: CompanyEmailDto;
  run: (fn: () => Promise<unknown>) => void;
  busy: boolean;
};

function DomainsSection({ companyId, data, run, busy }: SectionProps) {
  const unused = data.approvedDomains.filter((d) => !data.domains.some((x) => x.domain === d));
  const [domain, setDomain] = useState('');
  const [removing, setRemoving] = useState<CompanyEmailDto['domains'][number] | null>(null);
  // The last Verify result per domain: what the DNS showed for each record.
  const [checks, setChecks] = useState<Record<string, CompanyDnsRecordCheckDto[]>>({});
  const qc = useQueryClient();
  // Fias publishes the records into the Company's managed zone (ADR-024).
  // A record it could not place is said by name; the rest are applied by the
  // reconciler within minutes and verified by the sweep.
  const publish = useMutation({
    mutationFn: (d: CompanySendingDomainDto) =>
      api.publishCompanySendingDomainDns(companyId, d.domainId),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: queryKeys.companies.email(companyId) });
      // The Web tab's records editor shows the rows too.
      void qc.invalidateQueries({ queryKey: queryKeys.companies.web(companyId) });
      if (res.conflicts.length > 0) {
        toast({
          title: `${res.conflicts.length} of the records could not be published`,
          description: res.conflicts.map((c) => `${c.type} ${c.name}: ${c.reason}`).join('; '),
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Records published',
          description: 'Fias is adding them to your zone and will verify the domain shortly.',
        });
      }
    },
    onError: (err) => {
      // The zone may have gone since the card loaded (409): show the domain as
      // it stands now — its records to copy, if there is no zone any more.
      void qc.invalidateQueries({ queryKey: queryKeys.companies.email(companyId) });
      toast({ title: 'Could not publish', description: errorText(err), variant: 'destructive' });
    },
  });

  const verify = useMutation({
    mutationFn: (d: CompanySendingDomainDto) =>
      api.checkCompanySendingDomain(companyId, d.domainId),
    onSuccess: (res, d) => {
      setChecks((c) => ({ ...c, [d.domainId]: res.recordChecks }));
      if (res.domain?.status === 'verified') {
        toast({
          title: `${d.domain} is verified`,
          description: 'You can add sender addresses now.',
        });
      }
      void qc.invalidateQueries({ queryKey: queryKeys.companies.email(companyId) });
    },
    onError: (err) =>
      toast({ title: 'Could not verify', description: errorText(err), variant: 'destructive' }),
  });
  // A choice that has since been added (or un-approved) falls back to the first.
  const chosenNew = unused.includes(domain) ? domain : unused[0];

  return (
    <StepCard
      title="Sending domains"
      description="Add a domain Fias approved for you and publish its DNS records."
    >
      <p className="text-xs text-muted-foreground">
        To protect your domain&apos;s reputation, Fias pauses sending automatically once{' '}
        {HEALTH_BOUNCE_PERCENT}% of your mail bounces or {HEALTH_COMPLAINT_PERCENT}% is marked as
        spam over a week (counted once at least {HEALTH_MIN_RESULTS} emails have a final result), or
        if Amazon SES stops your sending. Fias staff review a paused domain before it resumes.
      </p>
      {data.domains.map((d) => (
        <div key={d.domainId} className="space-y-3 rounded-md border border-border p-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-foreground">{d.domain}</span>
            <Badge variant={d.status === 'verified' ? 'default' : 'secondary'}>
              {STATUS_LABEL[d.status]}
            </Badge>
            <div className="ml-auto flex gap-2">
              {d.status === 'pending_dns' && (
                <Button
                  size="sm"
                  disabled={busy || verify.isPending}
                  onClick={() => verify.mutate(d)}
                >
                  {verify.isPending && verify.variables?.domainId === d.domainId
                    ? 'Verifying…'
                    : 'Verify'}
                </Button>
              )}
              {(d.status === 'failed' || isStalledSetup(d)) && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={() => run(() => api.retryCompanySendingDomain(companyId, d.domainId))}
                >
                  {d.status === 'failed' ? 'Retry' : 'Retry setup'}
                </Button>
              )}
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => setRemoving(d)}>
                Remove
              </Button>
            </div>
          </div>
          {d.statusReason && <p className="text-xs text-destructive">{d.statusReason}</p>}
          {!data.approvedDomains.includes(d.domain) && (
            <p className="text-xs text-destructive">
              Fias no longer approves this domain for your Company, so it has stopped sending.
              {d.orphanedAt &&
                ` It will be removed on ${retiresOn(d.orphanedAt)} unless the approval returns.`}
            </p>
          )}
          {(d.status === 'verified' || d.status === 'paused') && (
            <DomainHealth stats={data.health?.find((h) => h.domainId === d.domainId)} />
          )}
          {d.records.length > 0 &&
            (d.managedZone
              ? // A verified domain still shows its records while one Fias should
                // have in the zone is not there (the owner deleted it), so it can
                // be published again before sending breaks.
                (d.status === 'pending_dns' ||
                  d.status === 'failed' ||
                  d.records.some((r) => r.required && !recordInZone(r))) && (
                  <ManagedDnsRecords
                    domain={d}
                    zoneName={d.managedZone.zoneName}
                    checks={checks[d.domainId]}
                    busy={busy}
                    onPublish={() => publish.mutate(d)}
                    publishing={publish.isPending && publish.variables?.domainId === d.domainId}
                  />
                )
              : (d.status === 'pending_dns' || d.status === 'failed') && (
                  <DnsRecords domain={d} checks={checks[d.domainId]} />
                ))}
        </div>
      ))}

      {unused.length > 0 && (
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            run(() => api.addCompanySendingDomain(companyId, chosenNew));
          }}
        >
          <div className="grid gap-1.5">
            <Label htmlFor="company-email-domain">Add a domain</Label>
            <select
              id="company-email-domain"
              className={SELECT_CLASS}
              value={chosenNew}
              onChange={(e) => setDomain(e.target.value)}
            >
              {unused.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>
          <Button type="submit" variant="outline" disabled={busy}>
            Add domain
          </Button>
        </form>
      )}
      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(o) => !o && setRemoving(null)}
        title={`Remove ${removing?.domain ?? ''}?`}
        description={
          removing?.managedZone
            ? `Its sender addresses stop working, arches using them stop sending, and Document Sign goes back to sending from Fias. Fias also removes the DNS records it published for it in ${removing.managedZone.zoneName} (DKIM, the bounce MX and SPF, and DMARC if Fias added it); records you edited yourself stay.`
            : 'Its sender addresses stop working, arches using them stop sending, and Document Sign goes back to sending from Fias.'
        }
        confirmLabel="Remove domain"
        destructive
        onConfirm={() => {
          const d = removing;
          setRemoving(null);
          if (d) run(() => api.removeCompanySendingDomain(companyId, d.domainId));
        }}
      />
    </StepCard>
  );
}

/**
 * An in-app confirmation. Never `window.confirm`: a browser can suppress
 * native dialogs ("prevent this page from creating additional dialogs"), and
 * then `confirm()` returns false at once — the button silently does nothing.
 */
function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  destructive = false,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  destructive?: boolean;
  onConfirm: () => void;
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className={destructive ? buttonVariants({ variant: 'destructive' }) : undefined}
            onClick={onConfirm}
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * "Send test": mails the signed-in admin — never anyone else — from this
 * address, so they can see it arrive. A new request key per click; a retried
 * request of the same click never mails twice.
 */
function SendTestButton({
  companyId,
  identityId,
  ready,
}: {
  companyId: string;
  identityId: string;
  ready: boolean;
}) {
  const send = useMutation({
    mutationFn: () => api.sendCompanyEmailTest(companyId, identityId, crypto.randomUUID()),
    onSuccess: (r) =>
      toast(
        r.status === 'failed'
          ? {
              title: 'The test was refused',
              description: 'Amazon SES would not send it. Check the domain’s status above.',
              variant: 'destructive',
            }
          : {
              title: `Test sent to ${r.to}`,
              description:
                r.status === 'unknown'
                  ? 'It may take a moment to be confirmed. Check your inbox and spam folder.'
                  : 'Check your inbox — and your spam folder if it is not there in a minute.',
            },
      ),
    onError: (err) =>
      toast({
        title: 'Could not send a test',
        description: errorText(err),
        variant: 'destructive',
      }),
  });
  return (
    <Button
      size="sm"
      variant="outline"
      className="ml-auto"
      disabled={!ready || send.isPending}
      title={
        ready
          ? 'Email yourself a test from this address. It is charged to your credits like any email.'
          : 'Available once the domain is verified'
      }
      onClick={() => send.mutate()}
    >
      Send test to me
    </Button>
  );
}

/**
 * Setup that never finished: pending, no DNS records yet, and older than the
 * server's STALLED_SETUP_AFTER_MS (company-mail-provisioning.ts) — only then
 * does the server accept a retry, so an in-flight setup is never doubled.
 */
const STALLED_SETUP_AFTER_MS = 5 * 60 * 1000;
function isStalledSetup(d: CompanySendingDomainDto): boolean {
  return (
    d.status === 'pending_dns' &&
    d.records.length === 0 &&
    !!d.createdAt &&
    Date.now() - new Date(d.createdAt).getTime() > STALLED_SETUP_AFTER_MS
  );
}

/** Mirrors COMPANY_EMAIL_OFFBOARDING_GRACE_DAYS (@fias/db-types). */
const OFFBOARDING_GRACE_DAYS = 30;
function retiresOn(orphanedAt: string): string {
  const at = new Date(new Date(orphanedAt).getTime() + OFFBOARDING_GRACE_DAYS * 86_400_000);
  return at.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** Mirrors the worker's thresholds (company-email-events.ts), as percentages. */
const HEALTH_BOUNCE_PERCENT = 5;
const HEALTH_COMPLAINT_PERCENT = 0.1;
const HEALTH_MIN_RESULTS = 100;

function percent(n: number, of: number): string {
  if (of === 0) return '—';
  const p = (n / of) * 100;
  return `${p < 1 && p > 0 ? p.toFixed(2) : p.toFixed(1)}%`;
}

function DomainHealth({ stats }: { stats: CompanyEmailDomainStatsDto | undefined }) {
  if (!stats || stats.sent === 0) {
    return <p className="text-xs text-muted-foreground">No email sent in the last 7 days.</p>;
  }
  return (
    <p className="text-xs text-muted-foreground">
      Last 7 days: {stats.sent} sent · {percent(stats.bounced, stats.settled)} bounced ·{' '}
      {percent(stats.complained, stats.settled)} marked as spam
    </p>
  );
}

/**
 * What each record is for, keyed by PURPOSE rather than type — the three DKIM
 * records are all CNAMEs, and the two TXT records do different jobs.
 */
function recordPurpose(
  purpose: CompanySendingDomainDto['records'][number]['purpose'],
  domain: CompanySendingDomainDto,
): { title: string; body: string } {
  switch (purpose) {
    case 'dkim':
      return {
        title: 'DKIM signature (CNAME)',
        body: `Lets receiving mail servers confirm that mail signed as ${domain.domain} really came from you and was not changed on the way. The three records point at the keys Amazon signs your mail with.`,
      };
    case 'mail_from_mx':
      return {
        title: 'Bounce handling (MX)',
        body: `Routes bounce notices for your mail to Amazon at ${domain.mailFromDomain}, so Fias can stop sending to addresses that do not exist. It only covers that subdomain — it does not change where your own email is delivered.`,
      };
    case 'mail_from_spf':
      return {
        title: 'Sender permission (SPF, TXT)',
        body: `Tells receiving servers that Amazon is allowed to send mail using ${domain.mailFromDomain} as the return address. It does not touch the SPF record on ${domain.domain} itself.`,
      };
    case 'dmarc':
      return {
        title: 'DMARC policy (TXT, recommended)',
        body: `Tells receiving servers what to do with mail that claims to be from ${domain.domain} but fails the checks above. "p=none" only asks for reports and blocks nothing — a safe starting point.`,
      };
  }
}

function RecordInfo({
  record,
  domain,
}: {
  record: CompanySendingDomainDto['records'][number];
  domain: CompanySendingDomainDto;
}) {
  const info = recordPurpose(record.purpose, domain);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          size="icon"
          variant="ghost"
          className="h-5 w-5 shrink-0 text-muted-foreground"
          aria-label={`What is this ${record.type} record for?`}
        >
          <Info className="h-3.5 w-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 text-xs">
        <p className="mb-1 font-semibold text-foreground">{info.title}</p>
        <p className="text-muted-foreground">{info.body}</p>
      </PopoverContent>
    </Popover>
  );
}

/** What each Verify state tells the Company, in their words. */
const CHECK_LABEL: Record<CompanyDnsRecordCheckDto['state'], { label: string; ok: boolean }> = {
  found: { label: 'Found', ok: true },
  missing: { label: 'Not found yet', ok: false },
  different: { label: 'Different value', ok: false },
  doubled: { label: 'Domain added twice', ok: false },
};

type ManagedRecord = CompanySendingDomainDto['records'][number];

/** What each state of a record in the Company's zone means to them. */
const PUBLISHED_LABEL: Record<NonNullable<ManagedRecord['published']>['state'], string> = {
  pending: 'Adding…',
  applied: 'In your zone',
  failed: 'Not applied',
  deleting: 'Removing…',
  present: 'Already in your zone',
  conflict: 'In the way',
};

/** The record is in the zone, by Fias's hand or the Company's own. */
function recordInZone(r: ManagedRecord): boolean {
  return r.published?.state === 'applied' || r.published?.state === 'present';
}

/**
 * The records for a domain whose zone Fias manages (ADR-024): nothing to copy
 * anywhere. Fias has put them in, or can, and says how each one stands. A
 * record not in the zone yet shows its value too — a Company resolving a
 * conflict in the records editor needs to know what the record should say.
 */
function ManagedDnsRecords({
  domain,
  zoneName,
  checks,
  busy,
  publishing,
  onPublish,
}: {
  domain: CompanySendingDomainDto;
  zoneName: string;
  checks?: CompanyDnsRecordCheckDto[];
  busy: boolean;
  publishing: boolean;
  onPublish: () => void;
}) {
  const published = domain.records.some(
    (r) => r.published && r.published.state !== 'present' && r.published.state !== 'conflict',
  );
  const checkFor = (r: ManagedRecord) =>
    checks?.find((c) => c.name === r.name && c.type === r.type);
  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        Fias manages the DNS for {zoneName}, so there is nothing to copy anywhere.{' '}
        {published
          ? 'Fias has published these records in your zone; verification usually completes within 15 minutes, and Fias keeps checking on its own.'
          : 'Fias can publish these records in your zone for you.'}{' '}
        A record in the way is one of your own at the same name with a different value: edit or
        delete it under Web → DNS records, then publish again.
      </p>
      <ul className="space-y-1 text-xs">
        {domain.records.map((r) => {
          const check = checkFor(r);
          return (
            <li key={`${r.type}-${r.name}`} className="space-y-0.5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono">{r.type}</span>
                <span className="font-mono text-muted-foreground">{r.host}</span>
                <RecordInfo record={r} domain={domain} />
                <span className="ml-auto text-muted-foreground">
                  {r.published ? PUBLISHED_LABEL[r.published.state] : 'Not published'}
                  {(r.published?.state === 'failed' || r.published?.state === 'conflict') &&
                    r.published.error &&
                    `: ${r.published.error}`}
                  {check && (
                    <span className={check.state === 'found' ? '' : 'text-destructive'}>
                      {' '}
                      · {CHECK_LABEL[check.state].label}
                    </span>
                  )}
                </span>
              </div>
              {!recordInZone(r) && (
                <p className="font-mono text-muted-foreground break-all">
                  {r.type === 'MX' ? `${r.priority ?? 10} ` : ''}
                  {r.value}
                </p>
              )}
            </li>
          );
        })}
      </ul>
      <Button size="sm" variant="outline" disabled={busy || publishing} onClick={onPublish}>
        {publishing ? 'Publishing…' : published ? 'Publish again' : 'Publish to my Fias zone'}
      </Button>
    </div>
  );
}

/**
 * The records a Company publishes, as a short how-to. Names are shown as the
 * HOST relative to the domain because that is what GoDaddy-style "Name"
 * fields want — pasting the full name there doubles the domain, the commonest
 * reason a domain never verifies (and a state Verify detects: `doubled`).
 */
function DnsRecords({
  domain,
  checks,
}: {
  domain: CompanySendingDomainDto;
  checks?: CompanyDnsRecordCheckDto[];
}) {
  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: 'Copied' });
    } catch {
      toast({ title: 'Could not copy', variant: 'destructive' });
    }
  };
  const checkFor = (r: CompanySendingDomainDto['records'][number]) =>
    checks?.find((c) => c.name === r.name && c.type === r.type);
  const required = domain.records.filter((r) => r.required);
  const allRequiredFound =
    checks !== undefined && required.every((r) => checkFor(r)?.state === 'found');
  const problems = checks?.filter((c) => c.state === 'different' || c.state === 'doubled') ?? [];

  const copyButton = (text: string, label: string) => (
    <Button
      size="icon"
      variant="ghost"
      className="h-6 w-6 shrink-0"
      aria-label={label}
      onClick={() => void copy(text)}
    >
      <Copy className="h-3.5 w-3.5" />
    </Button>
  );

  return (
    <div className="space-y-3">
      <ol className="list-decimal space-y-1 pl-5 text-xs text-muted-foreground">
        <li>
          Open the DNS settings for {domain.domain} where you manage it. In GoDaddy: My Products →{' '}
          {domain.domain} → DNS → Add New Record.
        </li>
        <li>
          Add each record below. Copy the Type, Name and Value exactly; for the MX record, put its
          Priority in the Priority field. Leave TTL at its default.
        </li>
        <li>
          Click Verify. Records usually show up within minutes; Amazon&apos;s own confirmation can
          take up to an hour. Fias keeps checking on its own.
        </li>
      </ol>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead className="text-muted-foreground">
            <tr>
              <th className="py-1 pr-3 font-medium">Type</th>
              <th className="py-1 pr-3 font-medium">Name</th>
              <th className="py-1 pr-3 font-medium">Value</th>
              <th className="py-1 pr-3 font-medium">Priority</th>
              {checks && <th className="py-1 font-medium">Status</th>}
            </tr>
          </thead>
          <tbody className="text-foreground">
            {domain.records.map((r) => {
              const check = checkFor(r);
              return (
                <tr
                  key={`${r.type}-${r.name}-${r.value}`}
                  className="border-t border-border align-top"
                >
                  <td className="py-1.5 pr-3 font-mono">
                    <div className="flex items-center gap-1">
                      {r.type}
                      <RecordInfo record={r} domain={domain} />
                    </div>
                    {!r.required && (
                      <div className="font-sans text-muted-foreground">recommended</div>
                    )}
                  </td>
                  <td className="py-1.5 pr-3">
                    <div className="flex items-start gap-1">
                      <span className="break-all font-mono">{r.host}</span>
                      {copyButton(r.host, `Copy name for ${r.type} ${r.name}`)}
                    </div>
                  </td>
                  <td className="py-1.5 pr-3">
                    <div className="flex items-start gap-1">
                      <span className="break-all font-mono">{r.value}</span>
                      {copyButton(r.value, `Copy value for ${r.type} ${r.name}`)}
                    </div>
                  </td>
                  <td className="py-1.5 pr-3 font-mono">{r.priority ?? ''}</td>
                  {checks && (
                    <td className="py-1.5 whitespace-nowrap">
                      {check && (
                        <Badge variant={CHECK_LABEL[check.state].ok ? 'default' : 'secondary'}>
                          {CHECK_LABEL[check.state].label}
                        </Badge>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">
        Name is shown without {domain.domain} — GoDaddy and most providers add it for you. If yours
        asks for the full name, add .{domain.domain} to the end.
      </p>
      {problems.some((c) => c.state === 'doubled') && (
        <p className="text-xs text-destructive">
          &ldquo;Domain added twice&rdquo;: that record was saved as its full name, so your provider
          added {domain.domain} again. Edit it and use just the Name shown above.
        </p>
      )}
      {problems.some((c) => c.state === 'different') && (
        <p className="text-xs text-destructive">
          &ldquo;Different value&rdquo;: something else is already published at that name. Change it
          to the value shown — for the TXT record, a name can have only one SPF record.
        </p>
      )}
      {allRequiredFound && domain.status === 'pending_dns' && (
        <p role="status" className="text-xs text-foreground">
          All required records are in place. Amazon usually confirms within an hour; this page
          updates when it does.
        </p>
      )}
      <p className="text-xs text-muted-foreground">
        None of these records change your existing email — they sign mail as {domain.domain} and
        route bounces to {domain.mailFromDomain}. If your inbound mail passes through a filtering
        gateway (such as Mimecast), allow mail signed with DKIM for {domain.domain} from Amazon SES,
        or email to your own team may be blocked as spoofed.
      </p>
    </div>
  );
}

function IdentitiesSection({ companyId, data, run, busy }: SectionProps) {
  const usable = data.domains.filter((d) => d.status !== 'removed');
  const [domainId, setDomainId] = useState('');
  const [localPart, setLocalPart] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const domainName = (id: string) => data.domains.find((d) => d.domainId === id)?.domain ?? '';

  const title = 'Sender addresses';
  const description = 'The From addresses your mail is sent as, and the name recipients see.';
  if (usable.length === 0) {
    return (
      <StepCard
        title={title}
        description={description}
        waitingFor="Available once you add a domain."
      />
    );
  }
  const chosenDomain = usable.some((d) => d.domainId === domainId) ? domainId : usable[0].domainId;

  return (
    <StepCard title={title} description={description}>
      {data.identities.length > 0 && (
        <p className="text-xs text-muted-foreground">
          &quot;Send test to me&quot; emails only you, and is charged to your credits like any
          email.
        </p>
      )}
      {data.identities.map((i) => (
        <div key={i.identityId} className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-foreground">
            {i.displayName} &lt;{i.localPart}@{domainName(i.domainId)}&gt;
          </span>
          {i.replyTo && <span className="text-muted-foreground">replies to {i.replyTo}</span>}
          <SendTestButton
            companyId={companyId}
            identityId={i.identityId}
            ready={
              data.domains.find((d) => d.domainId === i.domainId)?.status === 'verified' &&
              !data.pausedByFias
            }
          />
          <Button
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() => run(() => api.removeCompanySenderIdentity(companyId, i.identityId))}
          >
            Remove
          </Button>
        </div>
      ))}
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          run(async () => {
            await api.addCompanySenderIdentity(companyId, {
              domainId: chosenDomain,
              localPart: localPart.trim(),
              displayName: displayName.trim(),
              replyTo: replyTo.trim() || null,
            });
            setLocalPart('');
            setDisplayName('');
            setReplyTo('');
          });
        }}
      >
        <div className="grid gap-1.5">
          <Label htmlFor="company-email-local">Address</Label>
          <div className="flex items-center gap-1">
            <Input
              id="company-email-local"
              className="w-32"
              value={localPart}
              onChange={(e) => setLocalPart(e.target.value)}
              placeholder="team"
              maxLength={64}
            />
            <span className="text-sm text-muted-foreground">@</span>
            <select
              aria-label="Domain"
              className={SELECT_CLASS}
              value={chosenDomain}
              onChange={(e) => setDomainId(e.target.value)}
            >
              {usable.map((d) => (
                <option key={d.domainId} value={d.domainId}>
                  {d.domain}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="company-email-name">Name recipients see</Label>
          <Input
            id="company-email-name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            maxLength={120}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="company-email-reply">Replies to (optional)</Label>
          <Input
            id="company-email-reply"
            value={replyTo}
            onChange={(e) => setReplyTo(e.target.value)}
            maxLength={254}
          />
        </div>
        <Button
          type="submit"
          variant="outline"
          disabled={busy || !localPart.trim() || !displayName.trim()}
        >
          Add address
        </Button>
      </form>
    </StepCard>
  );
}

function AuthorizationsSection({ companyId, data, run, busy }: SectionProps) {
  const [archeId, setArcheId] = useState('');
  const [identityId, setIdentityId] = useState('');
  const [scope, setScope] = useState<'workspace' | 'company' | 'any'>('workspace');
  const [showMuteLine, setShowMuteLine] = useState(true);
  const [confirmingReplace, setConfirmingReplace] = useState(false);

  const title = 'Arches that send as you';
  const description = 'Which arches may send as which address, and how far their mail may reach.';
  if (data.identities.length === 0) {
    return (
      <StepCard
        title={title}
        description={description}
        waitingFor="Available once you add a sender address."
      />
    );
  }

  // The server names every arche that may send as the Company right now — the
  // first-party adopters, and approved arches whose approval covers the email
  // permission — so the picker needs no paging and no ids of its own. A choice
  // that has since disappeared (an arche un-approved, an address removed)
  // falls back to the default rather than submitting a stale id.
  const eligible = data.eligibleArches;
  const chosen =
    eligible.find((a) => a.archeId === archeId) ??
    eligible.find((a) => a.firstParty) ??
    eligible[0];
  const isPlugin = chosen !== undefined && !chosen.firstParty;
  const chosenIdentity = data.identities.some((i) => i.identityId === identityId)
    ? identityId
    : data.identities[0].identityId;
  const identityDomain = (id: string) => {
    const i = data.identities.find((x) => x.identityId === id);
    return i ? data.domains.find((x) => x.domainId === i.domainId) : undefined;
  };
  const chosenDomain = identityDomain(chosenIdentity);
  const existing = chosen && data.authorizations.find((a) => a.archeId === chosen.archeId);
  const archeLabel = (a: { archeId: string; archeName?: string | null }) =>
    a.archeName ?? a.archeId;
  const address = (id: string) => {
    const i = data.identities.find((x) => x.identityId === id);
    const d = i && data.domains.find((x) => x.domainId === i.domainId);
    return i && d ? `${i.localPart}@${d.domain}` : 'a removed address';
  };
  const senderName = data.identities.find((i) => i.identityId === chosenIdentity)?.displayName;
  const authorize = () => {
    if (!chosen) return;
    run(() =>
      api.authorizeCompanySender(companyId, {
        archeId: chosen.archeId,
        identityId: chosenIdentity,
        ...(isPlugin ? { recipientScope: scope, showMuteLine } : {}),
      }),
    );
  };

  return (
    <StepCard title={title} description={description}>
      {data.authorizations.map((a) => (
        <div key={a.authorizationId} className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-foreground">
            {archeLabel(a)} sends as {address(a.identityId)}
          </span>
          {a.recipientScope && (
            <Badge variant={a.recipientScope === 'any' ? 'destructive' : 'secondary'}>
              {SCOPE_BADGE[a.recipientScope]}
            </Badge>
          )}
          <Button
            size="sm"
            variant="ghost"
            className="ml-auto"
            disabled={busy}
            onClick={() => run(() => api.revokeCompanySender(companyId, a.authorizationId))}
          >
            Stop
          </Button>
        </div>
      ))}
      {chosen === undefined ? (
        <p className="text-sm text-muted-foreground">
          No arche can send as you yet. Approve an arche that asks to send Company email under Arche
          approvals, and it appears here.
        </p>
      ) : (
        <form
          className="space-y-3"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            if (existing) setConfirmingReplace(true);
            else authorize();
          }}
        >
          <div className="flex flex-wrap items-end gap-2">
            <div className="grid gap-1.5">
              <Label htmlFor="company-email-arche">Arche</Label>
              <select
                id="company-email-arche"
                className={SELECT_CLASS}
                value={chosen.archeId}
                onChange={(e) => setArcheId(e.target.value)}
              >
                {eligible.map((a) => (
                  <option key={a.archeId} value={a.archeId}>
                    {archeLabel(a)}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="company-email-as">Sends as</Label>
              <select
                id="company-email-as"
                className={SELECT_CLASS}
                value={chosenIdentity}
                onChange={(e) => setIdentityId(e.target.value)}
              >
                {data.identities.map((i) => (
                  <option key={i.identityId} value={i.identityId}>
                    {address(i.identityId)}
                  </option>
                ))}
              </select>
            </div>
            <Button type="submit" variant="outline" disabled={busy}>
              {existing ? 'Replace' : 'Allow'}
            </Button>
          </div>
          {chosenDomain && chosenDomain.status !== 'verified' && (
            <p role="status" className="text-xs text-destructive">
              {chosenDomain.domain} isn&apos;t sending yet ({STATUS_LABEL[chosenDomain.status]}).{' '}
              {isPlugin
                ? 'Until it is, this arche’s email is refused.'
                : `Until it is, ${archeLabel(chosen)} can’t send anything new in your Company’s workspaces.`}
            </p>
          )}

          {isPlugin ? (
            <fieldset className="space-y-2">
              <legend className="text-xs font-medium text-foreground">Who it may email</legend>
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="radio"
                  name="company-email-scope"
                  checked={scope === 'workspace'}
                  onChange={() => setScope('workspace')}
                />
                <span>
                  Members of the workspace it is used in
                  <span className="block text-xs text-muted-foreground">
                    Recommended. Keeps each workspace&apos;s mail inside that workspace.
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="radio"
                  name="company-email-scope"
                  checked={scope === 'company'}
                  onChange={() => setScope('company')}
                />
                <span>
                  Members of every workspace this Company links
                  <span className="block text-xs text-muted-foreground">
                    Mail written in one workspace (for example HR) can reach people in another (for
                    example Finance).
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="radio"
                  name="company-email-scope"
                  checked={scope === 'any'}
                  onChange={() => setScope('any')}
                />
                <span>
                  Anyone, at any address the arche supplies
                  <span className="block text-xs text-muted-foreground">
                    The arche can email people outside your Company from your domain. Every email
                    still carries a one-click unsubscribe (in the email&apos;s headers; the visible
                    line below is optional), but bounces and spam complaints count against your
                    domain and can pause all of your Company&apos;s email. Choose this only for an
                    arche you trust to send to outsiders.
                  </span>
                </span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={showMuteLine}
                  onChange={(e) => setShowMuteLine(e.target.checked)}
                />
                <span>
                  Show &ldquo;Sent by {archeLabel(chosen)} for {senderName}. Stop emails from this
                  arche&rdquo; at the bottom of its emails
                </span>
              </label>
            </fieldset>
          ) : (
            <p className="text-xs text-muted-foreground">
              {archeLabel(chosen)} is built by Fias and emails only the people its own actions
              involve, so there is no reach to choose.
            </p>
          )}
        </form>
      )}
      <ConfirmDialog
        open={confirmingReplace && existing !== undefined}
        onOpenChange={setConfirmingReplace}
        title={`Replace the address ${chosen ? archeLabel(chosen) : ''} sends as?`}
        description={
          existing && chosen
            ? `${archeLabel(chosen)} already sends as ${address(existing.identityId)}. Replace that with ${address(chosenIdentity)}?`
            : ''
        }
        confirmLabel="Replace address"
        onConfirm={() => {
          setConfirmingReplace(false);
          authorize();
        }}
      />
    </StepCard>
  );
}
